import { Check } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { useMemo, useState, type SubmitEventHandler } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  FieldError,
  Flex,
  ServerErrorCallout,
  Text,
  TextField,
} from '@/components/common';
import { AccountSubpageShell } from '@/components/layout';
import { useCurrentUser } from '@/contexts';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/services';
import { fieldLabelStyle } from '@/views/auth/styles';

type Translate = (key: string) => string;

const createChangeEmailSchema = (t: Translate) =>
  z
    .object({
      newEmail: z
        .string()
        .min(1, t('changeEmail.errors.emailRequired'))
        .check(z.email(t('changeEmail.errors.emailInvalid'))),
      confirmEmail: z
        .string()
        .min(1, t('changeEmail.errors.emailRequired'))
        .check(z.email(t('changeEmail.errors.emailInvalid'))),
    })
    .refine(data => data.newEmail === data.confirmEmail, {
      message: t('changeEmail.errors.emailMismatch'),
      path: ['confirmEmail'],
    });

type ChangeEmailFormValues = z.infer<ReturnType<typeof createChangeEmailSchema>>;

export function ChangeEmailPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentUser: profile } = useCurrentUser();
  const currentEmail = user?.email ?? profile?.email ?? '';
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [successEmail, setSuccessEmail] = useState<string | null>(null);

  const schema = useMemo(() => createChangeEmailSchema(key => t(key)), [t]);
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ChangeEmailFormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: { newEmail: '', confirmEmail: '' },
  });

  // Enable Save from live values; keep error messages blur-only (mode above).
  const values = useWatch({ control });
  const canSubmit = schema.safeParse(values).success;

  const goToAccount = () => {
    setSuccessEmail(null);
    navigate('/account/settings/account');
  };

  const onValid: SubmitEventHandler<HTMLFormElement> = handleSubmit(async data => {
    setSaveError(null);
    setSaving(true);
    try {
      await authService.changeEmail(data.newEmail);
      setSuccessEmail(data.newEmail.trim());
    } catch (err) {
      const key = err instanceof Error ? err.message : 'changeEmail.errors.saveFailed';
      setSaveError(t(key));
    } finally {
      setSaving(false);
    }
  });

  return (
    <AccountSubpageShell
      onBack={() => navigate('/account/settings/account')}
      title={t('changeEmail.title')}
      subtitle={t('changeEmail.subtitle')}
    >
      {saveError ? <ServerErrorCallout message={saveError} /> : null}

      <form onSubmit={onValid} noValidate>
        <Flex direction="column" gap="4">
          <Flex direction="column" gap="1">
            <Text size="1" color="gray" style={fieldLabelStyle}>
              {t('changeEmail.currentLabel')}
            </Text>
            <TextField.Root type="email" value={currentEmail} readOnly disabled />
          </Flex>

          <Flex direction="column" gap="1">
            <Text size="1" color="gray" style={fieldLabelStyle}>
              {t('changeEmail.newLabel')}
            </Text>
            <TextField.Root
              type="email"
              placeholder={t('changeEmail.placeholder')}
              autoComplete="email"
              inputMode="email"
              aria-invalid={errors.newEmail ? true : undefined}
              {...register('newEmail')}
            />
            <FieldError message={errors.newEmail?.message} />
          </Flex>

          <Flex direction="column" gap="1">
            <Text size="1" color="gray" style={fieldLabelStyle}>
              {t('changeEmail.confirmLabel')}
            </Text>
            <TextField.Root
              type="email"
              placeholder={t('changeEmail.placeholder')}
              autoComplete="email"
              inputMode="email"
              aria-invalid={errors.confirmEmail ? true : undefined}
              {...register('confirmEmail')}
            />
            <FieldError message={errors.confirmEmail?.message} />
          </Flex>

          <Button
            type="submit"
            color="gray"
            highContrast
            fullWidth
            size="lg"
            mt="2"
            disabled={!canSubmit || saving}
            isLoading={saving}
          >
            {t('changeEmail.save')}
          </Button>
        </Flex>
      </form>

      <Dialog.Root
        open={successEmail !== null}
        onOpenChange={open => {
          if (!open) goToAccount();
        }}
      >
        <Dialog.Content size="1" maxWidth="280px">
          <Flex direction="column" align="center" gap="3" py="2">
            <Flex
              align="center"
              justify="center"
              className="size-11 rounded-full border border-accent/40 bg-accent/12"
            >
              <Check className="text-accent" size={20} strokeWidth={1.6} aria-hidden />
            </Flex>
            <Dialog.Title align="center" size="3" weight="medium" mb="0">
              {t('changeEmail.successTitle')}
            </Dialog.Title>
            <Dialog.Description align="center" size="1" color="gray" mb="0">
              {t('changeEmail.successMessage', { email: successEmail ?? '' })}
            </Dialog.Description>
            <Button type="button" color="gray" highContrast fullWidth mt="2" onClick={goToAccount}>
              {t('changeEmail.successDone')}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </AccountSubpageShell>
  );
}
